/** Showing a candidate their deadline, in a zone they can act on.
 *
 */

import { t } from 'ttag'

const ZONE_LABELS: Record<string, () => string> = {
  'Europe/Oslo': () => t`Norway time`,
}

export const zoneLabel = (deadlineTz?: string | null): string | undefined =>
  deadlineTz ? (ZONE_LABELS[deadlineTz]?.() ?? deadlineTz) : undefined

export const DEADLINE_ZONE = 'Europe/Oslo'

export const todayInZone = (
  timeZone: string = DEADLINE_ZONE,
  now: Date = new Date(),
): string => {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone }).format(now)
  } catch {
    // An unknown zone, or a browser without zone support. The machine's own day is wrong for a
    // recruiter in another zone and right for everybody else, which is where this started.
    return new Intl.DateTimeFormat('en-CA').format(now)
  }
}

export const deadlineDay = (
  deadlineDate?: string | null,
): string | undefined => (deadlineDate ? deadlineDate.slice(0, 10) : undefined)

export type DeadlineFields = {
  /** The instant access ends: the first moment of the day after the chosen one. */
  deadline?: string | null
  /** The server-rendered sentence, in the candidate's language. */
  deadline_display?: string | null
  /** The IANA zone the deadline was resolved in, e.g. "Europe/Oslo". */
  deadline_tz?: string | null
  past_deadline?: boolean | null
}

/** The reader's own IANA zone, or undefined where the browser will not say.
 *
 * Browser only. No IP geolocation, here or anywhere: a VPN would silently change what a
 * candidate believes their deadline is, and it never influences the enforced instant.
 */
export const browserTimeZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

/** True when the reader is in the same zone the deadline was resolved in.
 *
 * `readerZone` defaults to the browser's, and is a parameter so the decision can be tested as
 * a pure function rather than by mocking `Intl`.
 */
export const readerSharesDeadlineZone = (
  deadlineTz?: string | null,
  readerZone = browserTimeZone(),
): boolean => Boolean(deadlineTz) && readerZone === deadlineTz

/** The deadline as a phrase to drop into a sentence, or undefined when there is none.
 *
 * Returns the server's sentence when the reader is in the vacancy's zone, and a concrete local
 * instant otherwise.
 */
export const formatDeadline = (
  fields: DeadlineFields | undefined | null,
  language = 'en',
  readerZone = browserTimeZone(),
): string | undefined => {
  const deadline = fields?.deadline
  const display = fields?.deadline_display

  if (!deadline) return display || undefined

  if (display && readerSharesDeadlineZone(fields?.deadline_tz, readerZone))
    return display

  const parsed = new Date(deadline)
  if (Number.isNaN(parsed.getTime())) return display || undefined

  try {
    return new Intl.DateTimeFormat(language, {
      // Explicit components, not dateStyle/timeStyle: the spec forbids combining those with
      // timeZoneName and throws a TypeError, which the catch below would quietly turn into a
      // sentence naming the wrong zone.
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
      ...(readerZone ? { timeZone: readerZone } : {}),
    }).format(parsed)
  } catch {
    // An unsupported locale tag or a browser without the options we asked for. The server's
    // sentence is wrong about the zone but right about the day, which beats showing nothing.
    return display || undefined
  }
}
